import React from 'react';
import { useTranslation } from 'react-i18next';
import * as Styled from './CallToActionButton.styled';
import { checkoutUrlBuilder } from '../../../features/checkout/checkoutUrlBuilder';
import { PRODUCT_TYPES } from '../../../features/cart/cart.model';
import { FREQUENCY } from '../../../models/DonationFrequency';
import { useAddCartItem } from '../../../features/cart/useAddCartItem';

// * If cart is enabled we dispatch the 'cart.add-item' custom event with item payload to cart
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
const CartButton = _ref => {
  let {
    blockState,
    dispatch,
    error,
    isLoading
  } = _ref;
  const {
    t
  } = useTranslation('blocks.impact');
  const {
    dispatchNewCartItem
  } = useAddCartItem();
  const isDisabled = isLoading || error;
  const handleAddCartItem = () => {
    // * Guard clause: return if no programDesignation or amountNumber exists
    if (!blockState.programDesignation || !blockState.amountNumber) {
      dispatch({
        type: 'TOUCH_ERRORABLE_INPUT',
        input: 'callToActionButton'
      });
      return;
    }
    const cartItem = {
      designationId: blockState?.programDesignation?.id,
      name: blockState?.programDesignation?.name,
      productType: blockState.frequency !== FREQUENCY.ONE_TIME ? PRODUCT_TYPES.RECURRING : PRODUCT_TYPES.DONATION,
      qty: 1,
      unitPrice: blockState?.amountNumber ?? 0
    };
    if (cartItem.productType === PRODUCT_TYPES.RECURRING) {
      // * Get user's current timezone
      const {
        timeZone
      } = Intl.DateTimeFormat().resolvedOptions();

      // * Only add endDate if user selects one
      const endDate = blockState.recurringEndDate && {
        endDate: blockState.recurringEndDate
      };

      // Note, start date will be assumed to be today, which is why it is not included.
      cartItem.recurringProperties = {
        frequency: blockState.frequency,
        timeZone,
        ...endDate
      };
    }
    dispatchNewCartItem(cartItem);
  };
  return /*#__PURE__*/_jsxs(Styled.CallToActionButton, {
    "data-id": "cp-impact-donate",
    disabled: isDisabled,
    onClick: !isDisabled ? handleAddCartItem : undefined,
    children: [/*#__PURE__*/_jsx(Styled.AddIcon, {}), t('CallToActionButton.CartButton.addToDonation', 'Add to donation')]
  });
};

// * If cart is not enabled we want to build and link to a checkout HREF

const CheckoutLinkButton = _ref2 => {
  let {
    blockState,
    campaignId = '',
    checkoutBaseUrl = '',
    forwardCheckoutQueryParams = {},
    orgId = '',
    error,
    isLoading
  } = _ref2;
  const {
    t
  } = useTranslation('blocks.impact');
  const isDisabled = isLoading || error || !blockState.amountNumber || !blockState?.programDesignation;
  const checkoutUrl = checkoutUrlBuilder(campaignId, orgId, forwardCheckoutQueryParams, checkoutBaseUrl, {
    amount: blockState?.amountNumber?.toFixed(2) || '0',
    frequency: blockState.frequency,
    recurringEndDate: blockState.recurringEndDate || '',
    currency: blockState.currencyCode || 'USD',
    designationId: String(blockState.programDesignation?.id || '')
  });
  return /*#__PURE__*/_jsx(Styled.CallToActionLink, {
    href: !isDisabled ? checkoutUrl : undefined,
    "aria-disabled": isDisabled ? 'true' : undefined,
    "data-id": "cp-impact-donate",
    role: "button",
    children: t('CallToActionButton.CheckoutLinkButton.donate', 'Donate')
  });
};

/*
 * Primary Impact Form Button that either adds to cart or goes to checkout depending
 * on the isCartEnabled prop. If cart is enabled, the button shows "Add to Cart",
 * otherwise it shows "Donate".
 */

export const CallToActionButton = _ref3 => {
  let {
    blockState,
    campaignId,
    checkoutBaseUrl,
    forwardCheckoutQueryParams,
    isCartEnabled,
    orgId,
    error = false,
    dispatch,
    isLoading = false
  } = _ref3;
  return /*#__PURE__*/_jsx(_Fragment, {
    children: isCartEnabled ? /*#__PURE__*/_jsx(CartButton, {
      blockState: blockState,
      error: error,
      dispatch: dispatch,
      isLoading: isLoading
    }) : /*#__PURE__*/_jsx(CheckoutLinkButton, {
      blockState: blockState,
      campaignId: campaignId,
      checkoutBaseUrl: checkoutBaseUrl,
      forwardCheckoutQueryParams: forwardCheckoutQueryParams,
      orgId: orgId,
      error: error,
      isLoading: isLoading
    })
  });
};